import type { RecurringItem } from '@/api/recurring'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { CategoryIcon } from '@/lib/categoryIcon'
import { formatDate, formatShortDate } from '@/lib/formatDate'
import {
  approxAmount,
  FREQUENCY_LABELS,
  getDefaultRecurringIcon,
  shareSummary,
} from '@/lib/recurring'
import { cn } from '@/lib/utils'
import { RotateCcw } from 'lucide-react'
import { dueBadge, isInStrip } from './dueLabel'
import { MarkDoneControl } from './MarkDoneControl'
import { RowMenu } from './RowMenu'
import { useRecurringActions } from './useRecurringActions'

function rowIcon(item: RecurringItem): string {
  return item.icon ?? getDefaultRecurringIcon(item.kind ?? 'OTHER', item.categoryIcon)
}

export function RecurringRow({
  item,
  onOpen,
  onEdit,
}: {
  item: RecurringItem
  onOpen: (item: RecurringItem) => void
  onEdit: (item: RecurringItem) => void
}) {
  const { format } = useFormatCurrency()
  const isReadOnly = useIsReadOnly()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const actions = useRecurringActions(item, { onEdit })
  const cancelled = !!item.cancelledAt
  const isIncome = item.type === 'INCOME'
  const badge = cancelled ? null : dueBadge(item.nextDueDate)
  const share = shareSummary(item)
  const missed = item.skips.length
  const late = badge?.tone === 'error'

  const sub = cancelled
    ? `${FREQUENCY_LABELS[item.frequency]} · Cancelled ${item.cancelledAt ? formatDate(item.cancelledAt) : ''}`
    : [
        FREQUENCY_LABELS[item.frequency],
        late
          ? `was due ${formatShortDate(item.nextDueDate)}`
          : `next ${formatShortDate(item.nextDueDate)}`,
        share?.kind === 'paidBy' ? `Paid by ${share.payer}` : item.financialAccountName,
      ]
        .filter(Boolean)
        .join(' · ')

  const owes =
    share?.kind === 'chipIn'
      ? share.others.length === 1
        ? `${share.others[0]} owes ${format(share.othersTotal)}`
        : `${share.others.length} people owe ${format(share.othersTotal)}`
      : null

  return (
    <div
      onClick={() => onOpen(item)}
      className={cn(
        'flex cursor-pointer items-center gap-3 border-t border-[var(--fl-divider)] py-3 first:border-t-0',
        cancelled && 'opacity-75'
      )}
    >
      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-[var(--fl-hero-bg)] text-[var(--fl-hero-fg)]">
        <CategoryIcon icon={rowIcon(item)} size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            className="truncate text-left text-sm font-medium text-[var(--fl-text-1)]"
            onClick={(e) => {
              e.stopPropagation()
              onOpen(item)
            }}
          >
            {item.name}
          </button>
          {badge && <Badge tone={badge.tone}>{badge.text}</Badge>}
          {share?.kind === 'chipIn' && <Badge tone="primary">Shared</Badge>}
          {missed > 0 && <Badge>{missed} missed</Badge>}
          {!isIncome && (item.lastPaymentChange ?? 0) > 0 && <Badge>Price up</Badge>}
        </div>
        <p className="mt-0.5 truncate text-xs text-[var(--fl-text-2)]">{sub}</p>
        {share && share.archived.length > 0 && (
          <p className="mt-0.5 text-xs text-[var(--fl-error)]">
            {share.archived.join(', ')} {share.archived.length === 1 ? 'is' : 'are'} archived —
            unarchive on People to mark this paid
          </p>
        )}
        {item.loanProgress && (
          <div className="mt-1.5 max-w-[220px]">
            <div className="h-[3px] overflow-hidden rounded-full bg-[var(--fl-surface-subtle)]">
              <div
                className="h-full rounded-full bg-[var(--fl-heading)] opacity-50"
                style={{
                  width: `${item.loanProgress.tenureMonths ? Math.min(100, Math.round((item.loanProgress.monthsPaid / item.loanProgress.tenureMonths) * 100)) : 0}%`,
                }}
              />
            </div>
            <p className="mt-0.5 text-xs text-[var(--fl-text-2)]">
              {format(item.loanProgress.outstanding)} outstanding
              {item.loanProgress.tenureMonths != null &&
                ` · ${item.loanProgress.monthsPaid} of ${item.loanProgress.tenureMonths} EMIs paid`}
            </p>
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p
          className={cn(
            'text-sm font-semibold tabular-nums',
            cancelled
              ? 'text-[var(--fl-text-2)]'
              : isIncome
                ? 'text-[var(--fl-income)]'
                : 'text-[var(--fl-error)]'
          )}
        >
          {isIncome && '+'}
          {approxAmount(item, format(item.amount))}
        </p>
        {owes ? (
          <p className="text-[11.5px] text-[var(--fl-text-2)]">{owes}</p>
        ) : (
          item.frequency !== 'MONTHLY' && (
            <p className="text-[11.5px] text-[var(--fl-text-2)]">
              {format(item.monthlyEquivalent)}/mo
            </p>
          )
        )}
      </div>
      {!isReadOnly && cancelled && (
        <Button
          variant="outline"
          onClick={(e) => {
            e.stopPropagation()
            actions.restore()
          }}
          disabled={actions.pending}
          title="Restore"
          className="h-auto shrink-0 gap-1 rounded-md px-3 py-1.5 text-xs"
        >
          <RotateCcw size={12} /> Restore
        </Button>
      )}
      {!isReadOnly && !cancelled && isInStrip(item.nextDueDate) && (
        <MarkDoneControl item={item} actions={actions} />
      )}
      {wide && (
        <RowMenu
          name={item.name}
          actions={actions}
          cancelled={cancelled}
          onOpen={() => onOpen(item)}
        />
      )}
      <div onClick={(e) => e.stopPropagation()}>{actions.dialogs}</div>
    </div>
  )
}
